import { useEffect } from "react";
import { Question, X } from "@phosphor-icons/react";
import type { GlossaryTerm } from "../types";

type HelpButtonProps = {
  term?: GlossaryTerm;
  onOpen: (term: GlossaryTerm) => void;
};

export function HelpButton({ term, onOpen }: HelpButtonProps) {
  if (!term) return null;
  return (
    <button
      type="button"
      className="help-button"
      aria-label={`${term.displayName}の説明を開く`}
      title={`${term.displayName}の説明`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(term);
      }}
    >
      <Question size={16} weight="bold" aria-hidden="true" />
    </button>
  );
}

type HelpModalProps = {
  term: GlossaryTerm | null;
  onClose: () => void;
};

export function HelpModal({ term, onClose }: HelpModalProps) {
  useEffect(() => {
    if (!term) return;
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKey);
    return () => document.removeEventListener("keydown", handleKey);
  }, [term, onClose]);

  if (!term) return null;
  return (
    <div className="help-backdrop" role="presentation" onMouseDown={onClose}>
      <section
        className="help-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="help-title"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className="help-dialog__header">
          <div>
            <p className="help-dialog__category">{term.category}</p>
            <h2 id="help-title">{term.displayName}</h2>
          </div>
          <button type="button" className="icon-button" onClick={onClose} aria-label="説明を閉じる" autoFocus>
            <X size={20} aria-hidden="true" />
          </button>
        </header>
        <p className="help-dialog__lead">{term.shortDescription}</p>
        <dl className="help-dialog__details">
          <div><dt>計算式</dt><dd>{term.formula}</dd></div>
          <div><dt>経営上の見方</dt><dd>{term.managementView}</dd></div>
          <div><dt>具体例</dt><dd>{term.example}</dd></div>
          <div><dt>注意点</dt><dd>{term.caution}</dd></div>
        </dl>
      </section>
    </div>
  );
}

